<template>
  <div id="offer-detail" class="p-l-15 p-r-15">
    <a class="back-page" :href="backHref" @click.prevent="onBack" v-if="showBackButton">
      {{ $t('Offer.back') }}
    </a>

    <a class="back-page" :href="backHrefHistory" @click.prevent="onBackHistory" v-else>
      {{ $t('Offer.back') }}
    </a>

    <div class="offer-title p-b-30">
      <h1 class="page-title clearfix">
        {{ $t('Offer.title_page', {number: offer.reference} ) }}
        <span class="pull-right p-r-30">
          <a v-if="$can('cancel', offer)" href="#cancel" class="btn btn-default btn-icon-cancel" @click.prevent="displayFormCancel">
            <i class="fa fa-times-circle"></i>
          </a>
          <a v-if="$can('share', offer)" href="#share" class="btn btn-default" @click.prevent="displaySendToForm">
            <i class="fa fa-envelope"></i>
          </a>
          <a v-if="canSendOfferStaff" href="#send_to_staff" class="btn btn-default" @click.prevent="sendOfferToStaff">
            <span>{{ $t('Offer.send_to_staff') }}</span>
          </a>
          <a v-if="$can('duplicate', offer)" href="#duplicate" class="btn btn-default" @click.prevent="duplicateOffer">
            <span>{{ $t('Offer.duplicate') }}</span>
          </a>
          <router-link :to="{ name: 'EditOffer', params: { entity_id: offer.entity.id, offer_id: offer.id }}" v-if="canUpdateOffer()" class="btn btn-default">
            <i class="fa fa-edit"></i>
          </router-link>
          <a v-if="$can('delete', offer)" href="#delete" class="btn btn-icon-dark" @click.prevent="deleteOffer">
            <i class="fa fa-trash-o"></i>
          </a>
          <router-link :to="{ name: 'EditOfferContract', params: { entity_id: offer.entity.id, offer_id: offer.id }}" v-if="canEditOfferContractFields" class="btn btn-default">
            {{ $t('Offer.edit_offer_contract') }}
          </router-link>
        </span>
      </h1>
      <div class="offer-publish-at" v-if="offer.published_at != null">
        {{ offer.published_by ?
          $t('Offer.publish_at_by', { date: convertToDate(offer.published_at), publisher: publisherFullName }) :
          $t('Offer.publish_at', { date: convertToDate(offer.published_at) })
        }}
      </div>
      <span v-if="offer.updated_by != null">
        <span> - </span>
        <span class="offer-updated-at">
          <b>{{ $t('Offer.updated') }}</b>
          {{ $t('Offer.cancel_at', { date: updatedAt }) }}
          {{ $t('Offer.by') }}
          {{ offer.updated_by }}
        </span>
      </span>
      <br>
      <span v-if="displayCancelled && isCancelledAt">
        <span> - </span>
        <span class="offer-cancelled-at">
          <b>{{ $t('Offer.cancelled') }}</b>
          {{ $t('Offer.cancel_at', { date: cancelledAt }) }}
          {{ $t('Offer.by') }}
          {{ offer.cancellor_name }}
        </span>
        <p class="offer-cancelled-comment" v-if="isComment" >
          {{ offer.comment }}
        </p>
      </span>
      <span class="clearfix"></span>
    </div>

    <div v-if="displayStats" class="row">
      <div v-if="displayCandidates" @click="checkSidebarToToggle('potential'); getPotentialCandidates()" class="col-md-4">
        <a href="#">
          <div class="stats">
            <span class="stats-icon bg-notice">
              <i class="fa-users fa"></i>
            </span>
            <div class="stats-content">
              <p class="m-0">
                <span class="stats-number">{{ potentialCandidatesCount + refusedCandidates.overlap_candidate_ids.length }}</span>
                <span class="stats-text">{{ $t('Offer.candidates') }}</span>
              </p>
              <i class="plus-icon m-r-10 m-t-5 fa-plus fa"></i>
            </div>
          </div>
        </a>
      </div>

      <div v-if="displayInterested" @click="checkSidebarToToggle('selecting'); getSelectingCandidates()" class="col-md-4">
        <a href="#" id="displayInterested">
          <div class="stats">
            <span class="stats-icon bg-success">
              <img src="../../assets/img/candidates_selected.png">
            </span>
            <div class="stats-content">
              <p class="m-0">
                <span class="stats-number">{{ selectingCandidatesCount }}</span>
                <span class="stats-text">{{ $t('Offer.selected_candidates') }}</span>
              </p>
              <i class="plus-icon m-r-10 m-t-5 fa-plus fa"></i>
            </div>
          </div>
        </a>
      </div>

      <div v-if="displayRefutes" @click="checkSidebarToToggle('refusing'); getRefusingCandidates()" class="col-md-4">
        <a href="#">
          <div class="stats">
            <span class="stats-icon bg-danger">
              <i class="fa-ban fa"></i>
            </span>
            <div class="stats-content">
              <p class="m-0">
                <span class="stats-number">{{ refutesCount }}</span>
                <span class="stats-text">{{ $t('Offer.refusingCandidates') }}</span>
              </p>
              <i class="plus-icon m-r-10 m-t-5 fa-plus fa"></i>
           </div>
          </div>
        </a>
      </div>
    </div>

    <candidates-list-sidebar :candidatesList="candidatesList"
                             :candidatesCount="candidatesCount"
                             :offer="offer"
                             :toggleCandidatesSidebar=toggleSidebar
                             :candidatesListType='candidatesListType'
                             @pageChanged="candidatesListPageChanged" />

    <offer-information :offer="offer"></offer-information>

    <div v-if="displayAppliedCandidates">
      <h4>{{ $t('Offer.candidate') }}</h4>
      <offer-candidate-info v-for="appliedCandidate in offer.applied_candidates"
                            :offerStatus="offer.status"
                            :appliedCandidate="appliedCandidate"
                            :entityId="offer.entity.id"
                            :key="appliedCandidate.user_info.id"
                            :validator="validatorFullName"
                            :offerId="offer.id"
                            :offerStartAt="offer.start_at" />
    </div>

    <div v-if="displayRefusedCandidates" class="container container-fixed-lg m-t-20">
      <div class="col-lg-6">
        <refused-candidates :candidates="refusedCandidates.list"
                            :overlapCandidateIds="refusedCandidates.overlap_candidate_ids" />
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
  import { mapGetters, mapActions } from 'vuex'
  import OfferInformation from './OfferInformation'
  import OfferCandidateInfo from './OfferCandidateInfo'
  import RefusedCandidates from './RefusedCandidates'
  import OfferSharingForm from './OfferSharingForm'
  import OfferCancelForm from './OfferCancelForm'
  import moment from 'moment'
  import CandidatesListSidebar from "./CandidatesListSidebar";
  import { firstOfferPeriod, lastOfferPeriod, getPropertyFromObject } from '@/helpers/offers'
  import { canEditOfferContractFields } from '@/helpers/contracts'
  import ie_mixin from '@/mixin/ie'
  import routesMixin from '@/mixin/routes'
  import AtInternetManager from '@/helpers/at_internet/manage_requests'

  const OFFER_SHARING_VIEW_NAME = 'OfferSharingForm'
  const OFFER_CANCEL_VIEW_NAME = 'OfferCancelForm'

  export default {
    name: 'Offer',
    mixins: [ie_mixin, routesMixin],
    data: function(){
      return {
        toggleSidebar: false,
        currentSidebar: '',
        candidatesListType: 'potential',
        candidatesCount: 0,
      }
    },
    components: {
      OfferInformation,
      OfferCandidateInfo,
      CandidatesListSidebar,
      RefusedCandidates
    },
    computed: {
      ...mapGetters({
        offer: 'offers/offer',
        currentUser: 'auth/currentUser',
        potentialCandidatesCount: 'offers/potentialCandidatesCount',
        candidatesList: 'offers/candidatesList',
        refutesCount: 'offers/refutesCount',
        refusedCandidatesCount: 'offers/refusedCandidatesCount',
        refusedCandidates: 'offers/refusedCandidates',
        selectingCandidatesCount: 'offers/selectingCandidatesCount',
        managerOptions: 'users/managersForSharingOptions',
        canSendOfferStaff: 'offers/canSendOfferStaff'
      }),
      offerId() {
        return this.$route.params.id;
      },
      expired() {
        return this.offer.end_at < moment().format()
      },
      publisherFullName() {
        if (!this.offer.published_by) {
          return ''
        }
        return `${this.offer.published_by.first_name} ${this.offer.published_by.last_name}`
      },
      validatorFullName() {
        if (!this.offer.validated_by) {
          return ''
        }
        return `${this.offer.validated_by.first_name} ${this.offer.validated_by.last_name}`
      },
      cancelledAt() {
        return this.$options.filters.formatDate(this.offer.cancelled_at);
      },
      updatedAt() {
        return this.$options.filters.formatDate(this.offer.updated_at);
      },
      isCancelledAt() {
        return this.offer.cancelled_at != null
      },
      isComment() {
        return this.offer.comment != null
      },
      appliedCandidateCount(){
        return (this.offer.applied_candidates || []).length
      },
      displayStats() {
        return this.offer.status != 'draft'
      },
      displayCandidates() {
        return !this.expired && (this.offer.status == 'published' || this.offer.status == 'waiting')
      },
      displayRefutes() {
        return this.offer.status == 'published' || this.offer.status == 'waiting' || this.offer.status == 'validated'
      },
      displayInterested() {
        return !this.expired && (this.offer.status == 'published' || this.offer.status == 'waiting')
      },
      displayCancelled(){
        return this.offer.status == 'cancelled'
      },
      displayAppliedCandidates(){
        return this.appliedCandidateCount != 0
      },
      displayRefusedCandidates() {
        return this.refusedCandidatesCount && this.refusedCandidatesCount > 0
      },
      canEditOfferContractFields() {
        return canEditOfferContractFields(this.offer, this.currentUser, this.offer.entity.id)
      },
      showBackButton() {
        return !this.expired
      },
      backHref() {
        var { href } = this.$router.resolve({ name: 'Offers' }, this.$route, true)
        return href
      },
      backHrefHistory() {
        var { href } = this.$router.resolve({ name: 'RequestHistories' }, this.$route, true)
        return href
      }
    },
    methods:{
      ...mapActions({
        deleteOfferAction: 'offers/deleteOffer',
        setCandidatesListPageAction: 'offers/setCandidatesListPage',
        getPotentialCandidatesAction: 'offers/getPotentialCandidates',
        getRefusingCandidatesAction: 'offers/getRefusingCandidates',
        getSelectingCandidatesAction: 'offers/getSelectingCandidates',
        getOfferAction: 'offers/getOffer',
        getNumberOfRefutesAction: 'offers/getNumberOfRefutes',
        getNumberPotentialCandidatesAction: 'offers/getNumberPotentialCandidates',
        initManagersForSharingOptionsAction: 'users/initManagersForSharingOptions',
        getRefusedCandidatesCountAction: 'offers/getRefusedCandidatesCount',
        getRefusedCandidatesAction: 'offers/getRefusedCandidates',
        getSelectingCandidatesCountAction: 'offers/getSelectingCandidatesCount',
        shareOfferToSelectedManagersAction: 'users/shareOfferToSelectedManagers',
        enable_message: 'message/enable',
        canSendOfferToStaff: 'offers/canSendOfferToStaff',
        setKeepOfferFilters: 'offers/setKeepFilters',
      }),
      convertToDate(date) {
        return this.$options.filters.formatDate(date);
      },
      canUpdateOffer() {
        if (this.offer.status === 'validated') {
          return this.expired;
        } else if (this.offer.status === 'cancelled' ||
                   this.offer.status === 'waiting' ||
                   (this.offer.status === "published" && this.expired)) {
          return false;
        } else {
          return this.$can('update', 'Offer');
        }
      },
      deleteOffer: function(dialog) {
        var self = this
        this.$dialog.confirm(this.$i18n.t('Offer.delete_confirmation'))
        .then(function () {
          self.deleteOfferAction(self.offer.id).then(response => {
            if(response.status == 200){
              AtInternetManager.sendValidateDeleteRequestEvent()
              self.$router.push({ name: 'Offers' });
              var message = { type: 'success', content: self.$i18n.t('messages.offer_deleted') };
              self.$store.dispatch('message/enable', message);
            }
          });
        })
        .catch(function () {})
      },
      duplicateOffer: function(dialog) {
        if (this.isIE) {
          if (confirm(this.$i18n.t('Offer.duplicate_confirmation'))) {
            this.$router.push({ name: 'NewOffer', params: { entity_id: this.offer.entity.id, original_offer_id: this.offer.id } })
          } else {
          }
        } else {
          var self = this
          this.$dialog.confirm(this.$i18n.t('Offer.duplicate_confirmation'))
          .then(function () {
            self.$router.push({ name: 'NewOffer', params: { entity_id: self.offer.entity.id, original_offer_id: self.offer.id } })
          })
          .catch(function () {})
        }
      },
      checkSidebarToToggle: function(sidebarType) {
        this.setCandidatesListPageAction(1)
        if (this.currentSidebar == sidebarType && this.toggleSidebar == true) {
          this.toggleSidebar = false;
        } else {
          this.toggleSidebar = true;
        }
        this.currentSidebar = sidebarType;
      },
      getPotentialCandidates: function(page = 1) {
        this.candidatesListType = 'potential';
        this.getPotentialCandidatesAction({
          offer: {
            id: this.offer.id,
            resource_type: this.offer.resource_type,
            mode: this.offer.mode,
            replaced_service_id: this.offer.replaced_service_id,
            searched_occupation_ids: this.offer.searched_occupations.map(occupation => occupation.id),
            offer_periods_attributes: this.offer.offer_periods_attributes,
            include_proposed_candidates: this.offer.include_proposed_candidates,
            include_hub_services: this.offer.include_hub_services,
            entity_id: this.offer.entity.id
          },
          include_refuse_candidates: true,
          page: page
        })
        this.setCandidatesListPageAction(page)
        this.candidatesCount = this.potentialCandidatesCount
      },
      getRefusingCandidates: function(page = 1) {
        this.candidatesListType = 'refusing'
        this.getRefusingCandidatesAction({ id: this.offerId, page: page })
        this.setCandidatesListPageAction(page)
        this.candidatesCount = this.refutesCount
      },
      getSelectingCandidates: function(page = 1) {
        this.candidatesListType = 'selecting'
        this.getSelectingCandidatesAction({ id: this.offerId, page: page })
        this.setCandidatesListPageAction(page)
        this.candidatesCount = this.selectingCandidatesCount
      },
      candidatesListPageChanged: function(page) {
        if (this.candidatesListType == 'refusing') {
          this.getRefusingCandidates(page)
        } else if (this.candidatesListType == 'selecting') {
          this.getSelectingCandidates(page)
        } else {
          this.getPotentialCandidates(page)
        }
      },
      displaySendToForm() {
        var self = this;
        this.$dialog.alert("share offer", {
          view: OFFER_SHARING_VIEW_NAME,
          html: true,
          animation: 'fade',
          backdropClose: true,
          managerOptions: self.managerOptions
        })
        .then(dialog =>{
          this.shareOfferToSelectedManagersAction({sendingList: dialog.data, offer_id: self.offer.id})
          var message = { type: 'success', key: 'messages.offer_shared' }
          this.enable_message({ type: message.type, content: this.$i18n.t(message.key) })
        })
      },
      displayFormCancel() {
        this.$dialog.alert("offer cancel", {
          view: OFFER_CANCEL_VIEW_NAME,
          html: true,
          animation: 'fade',
          backdropClose: true,
          offerId: this.offer.id,
          store: this.$store
        })
      },
      sendOfferToStaff() {
        this.$router.push({ name: 'OfferSendToStaff', params: { offer_id: this.offer.id } })
      },
      onBack(event) {
        this.setKeepOfferFilters(true)
        this.$router.push({ name: 'Offers' })
      },
      onBackHistory(event) {
        this.setKeepOfferFilters(true)
        this.$router.push({ name: 'RequestHistories' })
      }
    },
    mounted: async function() {
      await this.getOfferAction(this.offerId);
      this.setKeepOfferFilters(true)
      this.getNumberOfRefutesAction(this.offerId)
      this.initManagersForSharingOptionsAction()
      this.getNumberPotentialCandidatesAction({
        offer: {
            resource_type: this.offer.resource_type,
            mode: this.offer.mode,
            replaced_service_id: this.offer.replaced_service_id,
            searched_occupation_ids: this.offer.searched_occupations.map(occupation => occupation.id),
            offer_periods_attributes: this.offer.offer_periods_attributes,
            include_proposed_candidates: this.offer.include_proposed_candidates,
            include_hub_services: this.offer.include_hub_services,
            entity_id: this.offer.entity.id
        }
      });
      this.getRefusedCandidatesCountAction(this.offerId);
      this.getRefusedCandidatesAction(this.offerId);
      this.getSelectingCandidatesCountAction(this.offerId)
      this.$dialog.registerComponent(OFFER_SHARING_VIEW_NAME, OfferSharingForm);
      this.$dialog.registerComponent(OFFER_CANCEL_VIEW_NAME, OfferCancelForm);
      this.canSendOfferToStaff(this.offerId);
    }
  }
</script>

<style lang="scss" scoped>

.plus-icon {
  float: right;
}

.offer-publish-at, .offer-update-at {
  font-size: 0.8rem;
}

.offer-publish-at {
  opacity: 0.7;
  font-style: italic;
}

.offer-update-at  {
  color: #39B8FF;
}

.offer-cancelled-at, .offer-updated-at{
  opacity: 0.8;
  font-style: italic;
  font-size: 0.8rem;
}

.offer-cancelled-at{
  color: #FF5722;
}

.offer-updated-at {
  color: #3BAEFF;
}

.offer-cancelled-comment {
  opacity: 0.8;
  font-style: italic;
  font-size: 0.8rem;
  color: #FF5722;
  border-left: 1px solid #FF5722;
  padding-left: 5px;
  margin-top: 5px;
}

.stats{
  display: flex;
  background: #fff;
  height: 60px;

  border: 1px solid #dedfe1;
  border-radius: 3px;
  margin-bottom: 15px;
  .stats-icon{
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-top-left-radius: 2px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 2px;
    min-width: 60px;
    font-size: 32px;
    background: rgba(0,0,0,0.2);
    color: white;
    opacity: 0.6;
  }
  i{
    display: inline-block;
    line-height: 1;
  }
  .stats-content{
    display: flex;
    align-items: center;
    flex: 1;
    padding: 4px 8px;
    i {
      margin-left: auto;
    }
  }
  .stats-icon img {
    display: block;
    height: 60px;
    width: 60px;

    border-top-left-radius: 2px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 2px;
  }
  .stats-number{
    display: inline-block;
    font-weight: bold;
    font-size: 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/offers/Offer.vue